<template>
  <div ref="contenedorCollage" class="collage-carousel">
    <!-- CENEFAS ONDULADAS ESTILO EDITORIAL (Arriba y Abajo) -->
    <div class="rasgado rasgado-superior">
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="svg-onda">
        <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,0 L0,0 Z" fill="#1A1A1A"></path>
      </svg>
    </div>
    
    <div ref="bgRef" class="bg-rosa"></div>

    <!-- FLORES ORNAMENTALES -->
    <img src="@/assets/florcoco1.png" class="flor flor-lt" alt="" />
    <img src="@/assets/florcoco1.png" class="flor flor-rb" alt="" />

    <!-- GRID DE FOTOS -->
    <div class="collage-grid">
      <div
        v-for="(image, index) in images"
        :key="index"
        :ref="el => { if (el) listaFotos[index] = el }"
        class="collage-item"
        @click="enlargeImage(index)"
      >
        <img
          :src="image"
          class="collage-image"
          :alt="'Imagen ' + index"
        />
      </div>
    </div>

    <!-- MODAL DE IMAGEN AMPLIADA -->
    <div
      v-if="isImageEnlarged"
      class="image-modal"
      @click="closeModal"
    >
      <img
        :src="images[currentIndex]"
        class="enlarged-image"
        alt="Imagen ampliada"
      />
    </div>

    <div class="rasgado rasgado-inferior">
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="svg-onda">
        <path d="M0,80 C300,140 600,10 900,100 C1050,145 1150,70 1200,80 L1200,120 L0,120 Z" fill="#1A1A1A"></path>
      </svg>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import gsap from 'gsap'

export default {
  name: 'CollageAnimadoMagico',
  setup() {
    // 🔥 Imagen mantenida intacta
    const images = [
      require('@/assets/16_11zon (1).webp'),
            require('@/assets/6_11zon.webp'),

    ]

    // Refs de control local aisladas
    const contenedorCollage = ref(null)
    const bgRef = ref(null)
    const listaFotos = ref([]) 

    const currentIndex = ref(0)
    const isImageEnlarged = ref(false)
    
    let observadorVisible = null
    let ctxGsap = null

    const enlargeImage = (index) => {
      currentIndex.value = index
      isImageEnlarged.value = true
    }

    const closeModal = () => {
      isImageEnlarged.value = false
    }

    onMounted(async () => {
      await nextTick()
      if (!contenedorCollage.value) return

      // Selector scoped exclusivo para este componente
      const $ = gsap.utils.selector(contenedorCollage.value)
      const flores = $('.flor')

      // Limpiamos slots vacíos del array de fotos por seguridad
      const fotosFiltradas = listaFotos.value.filter(Boolean)

      // ESTADOS INICIALES ESTRICTOS (Garantizan invisibilidad antes de animar)
      gsap.set(bgRef.value, { opacity: 0 })
      gsap.set(flores, { opacity: 0, scale: 0.8, y: 30 })
      // Estado editorial para la foto: un poco más abajo, invisible y ligeramente más pequeña
      gsap.set(fotosFiltradas, { opacity: 0, y: 60, scale: 0.9, filter: 'blur(5px)' })

      // IntersectionObserver optimizado para el Viewport móvil
      observadorVisible = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return

          // Encapsulamos en contexto para blindar las animaciones en Vue
          ctxGsap = gsap.context(() => {
            const tl = gsap.timeline({ 
              defaults: { 
                ease: 'power4.out', // Transición ultra suave tipo editorial
                lazy: false // Renderizado inmediato para evitar saltos en móviles
              } 
            })

            // 1. Entrada suave y elegante del fondo rosa
            tl.to(bgRef.value, {
              opacity: 1,
              duration: 0.8
            })

            // 2. Revelado orgánico de las flores ornamentales
            .to(flores, {
              opacity: 0.7, // Un poco más visibles para resaltar
              scale: 1,
              y: 0,
              duration: 1,
              stagger: 0.2 // Cascada suave
            }, '-=0.5') // Solapado con el fondo

            // 3. 🔥 ENTRADA EDITORIAL MÁGICA DE LA FOTO
            .to(fotosFiltradas, {
              opacity: 1,
              y: 0,
              scale: 1, // Vuelve a su tamaño original suavemente
              filter: 'blur(0px)', // Se enfoca al entrar
              duration: 1.2,
              stagger: 0.3 // Efecto cascada premium si hay más fotos
            }, '-=0.7') // Empieza antes de que terminen las flores

            // Bucle infinito orgánico de flotación para las flores (No consume casi CPU)
            flores.forEach((flor, i) => {
              gsap.to(flor, {
                y: `+=${gsap.utils.random(10, 18)}`,
                duration: gsap.utils.random(4, 6),
                repeat: -1,
                yoyo: true,
                ease: 'sine.inOut',
                delay: i * 0.3
              })
            })

          }, contenedorCollage.value)

          // Desconectamos para que la animación de entrada se ejecute una sola vez
          observadorVisible.disconnect()
        })
      }, { 
        threshold: 0.15, // Se activa en cuanto asoma el 15% en el móvil
        rootMargin: "0px 0px -10% 0px" // Margen de seguridad inferior
      })

      observadorVisible.observe(contenedorCollage.value)
    })

    onUnmounted(() => {
      if (observadorVisible) observadorVisible.disconnect()
      if (ctxGsap) ctxGsap.revert()
    })

    return {
      images,
      contenedorCollage,
      bgRef,
      listaFotos,
      currentIndex,
      isImageEnlarged,
      enlargeImage,
      closeModal
    }
  }
}
</script>

<style scoped>
.collage-carousel {
  position: relative;
  width: 100%;
  /* Mantenido para asegurar visibilidad en móviles */
  min-height: 80vh; 
  margin-bottom: -2px; /* Ajuste para evitar líneas blancas entre secciones */
  overflow: hidden;
  background: #ffffff; /* Color base antes de que cargue el rosa */
}

/* FONDO ROSA ANIMADO */
.bg-rosa {
  position: absolute;
  inset: 0;
  background: #eaadc0;
  z-index: 0;
  will-change: opacity;
}

/* 🌸 FLORES ORNAMENTALES */
.flor {
  position: absolute;
  width: 110px; /* Un poco más grandes para look editorial */
  max-width: 48vw;
  opacity: 0;
  pointer-events: none;
  z-index: 1;
  will-change: transform, opacity;
}

/* Posiciones mantenidas intactas */
.flor-lt { top: 2vh; left: -35px; transform: rotate(-15deg) scaleX(-1);
 }
.flor-rb { bottom: 28vh; right: -35px;  transform: rotate(35deg);}

/* GRID DE FOTOS */
.collage-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  padding-top: 10vh; /* Espacio para el rasgado superior */
  padding-bottom: 10vh; /* Espacio para el rasgado inferior */
}

.collage-item {
  grid-column: span 12;
  opacity: 0; /* Controlado estrictamente por GSAP */
  cursor: pointer;
  margin-bottom: 4vh; /* Espaciado editorial más limpio */
  display: flex;
  justify-content: center;
  will-change: transform, opacity, filter;
}

.collage-image {
  width: 88%; /* Ancho estilo maquetación */
  height: auto;
  margin: 0 auto;
  border-radius: 4px; /* Bordes ligeramente suavizados tipo papel */
  box-shadow: 0 8px 25px rgba(0,0,0,0.15); /* Sombra elegante */
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}

/* Efecto hover suave mantenido */
.collage-image:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 30px rgba(0,0,0,0.2);
}

/* MODAL DE AMPLIACIÓN */
.image-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 15, 0.95); /* Fondo más oscuro para resaltar */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.enlarged-image {
  max-width: 92%;
  max-height: 92%;
  border-radius: 8px;
  box-shadow: 0 0 40px rgba(0,0,0,0.5);
}

/* ===== NUEVOS RASGADOS ESTILO ONDAS ELEGANTES (SVG) ===== */
.rasgado {
  position: absolute;
  left: 0;
  width: 100%;
  height: 60px; /* Un poco más altos para que se noten bien */
  z-index: 12;
  pointer-events: none;
}

.svg-onda {
  width: 100%;
  height: 100%;
  display: block;
}

.rasgado-superior {
  top: 0;
  /* Espacio negativo para montarse sutilmente sobre la sección anterior si es necesario */
  margin-top: -1px; 
}

.rasgado-inferior {
  bottom: 0;
  /* Espacio negativo para montarse sutilmente sobre la sección siguiente */
  margin-bottom: -1px; 
}
</style>